import { ArrowRightIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@shadcn/components/sheet';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import changelogItems, {
  ChangelogEntry,
  tagLabels,
  tagStyles,
} from './changelogItems';

const PREVIEW_COUNT = 10;

function SheetEntryCard({ entry }: { entry: ChangelogEntry }) {
  return (
    <div className="border-b last:border-b-0 py-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs text-muted-foreground">
          {dayjs(entry.date).format('DD MMM YYYY')}
        </span>
        {entry.tags?.map((tag) => (
          <Badge
            key={tag}
            variant="outline"
            className={`text-xs py-0 ${tagStyles[tag] || ''}`}
          >
            {tagLabels[tag] || tag}
          </Badge>
        ))}
      </div>
      <h3 className="text-sm font-semibold mb-0.5">{entry.title}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {entry.summary}
      </p>
    </div>
  );
}

interface ChangelogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ChangelogSheet({
  open,
  onOpenChange,
}: ChangelogSheetProps) {
  const navigate = useNavigate();
  const recentItems = changelogItems.slice(0, PREVIEW_COUNT);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[400px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-2">
          <SheetTitle>What's new</SheetTitle>
          <SheetDescription>Recent updates and improvements</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6">
          {recentItems.map((entry) => (
            <SheetEntryCard key={entry.id} entry={entry} />
          ))}
        </div>

        <div className="px-6 py-4 border-t">
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              onOpenChange(false);
              navigate('/dashboard/changelog');
            }}
          >
            View full changelog
            <ArrowRightIcon size={16} />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
